/**
 * Per-tenant-and-user MCP connection registry.
 *
 * Holds: which remote MCP servers the user has connected, the OAuth
 * client_id we registered for each, and the current access/refresh
 * tokens. Persisted in localStorage under the authenticated tenant and
 * user owner so another user in the tenant cannot inherit credentials.
 *
 * Connection record shape:
 *   {
 *     serverId,            // "linear"
 *     config,              // per-connection endpoint config (configFields servers only)
 *     clientId,            // from dynamic client registration
 *     registration,        // full DCR response (kept for debug / re-registration)
 *     metadata,            // RFC 8414 authorization-server metadata
 *     tokens: {
 *       accessToken, refreshToken, expiresAt, tokenType,
 *       refreshFailedAt,   // set when the last refresh was rejected
 *     },
 *     lastError,           // { message, at } from the last failed handshake, or null
 *     connectedAt,
 *   }
 *
 * Add servers by extending SERVER_REGISTRY. OAuth servers need
 * issuer/mcpUrl (static, or derived per connection via configFields);
 * apiKey servers need mcpUrl + credentialFields.
 */

import {
  fetchAuthMetadata,
  registerClient,
  startAuthorize,
  refreshTokens,
} from "./oauth.js";
import {
  browserStorageOwnerIsCurrent,
  currentBrowserStorageOwner,
} from "../browserStorageScope.js";

const STORAGE_PREFIX = "mcp:v2:";
const LEGACY_STORAGE_PREFIX = "mcp:";
const CHANGE_EVENT = "mcp:connections-changed";
// Fired for bookkeeping writes (lastError, refresh outcome) that change
// what the picker shows but not which servers are connected. Kept apart
// from CHANGE_EVENT because the chat rebuilds its agent on that one, and
// a handshake failure is observed from inside an agent build.
const STATUS_EVENT = "mcp:connection-status-changed";

// Remote MCP servers we expose in the picker.
//
// Two auth flavours are supported:
//   `auth: "oauth"` (default) — full OAuth dance via DCR. Needs:
//     issuer  — origin that serves `/.well-known/oauth-authorization-server`
//     mcpUrl  — the streamable-HTTP MCP endpoint (POST + SSE)
//   `auth: "apiKey"` — the user pastes a static credential. Needs:
//     mcpUrl           — streamable-HTTP endpoint; may contain {fieldKey}
//                        placeholders that get filled from stored creds
//                        at connect time (e.g. Firecrawl puts the key
//                        in the URL path; Tavily puts it in the query
//                        string).
//     credentialFields — [{ key, label, placeholder?, helpUrl?, prefix? }]
//                        Whatever the user has to paste. Most servers
//                        just want one `apiKey` field.
//     bearerField      — Optional; name of the field to send as
//                        `Authorization: Bearer <value>`. Omit when
//                        the URL template already carries the credential.
//   `auth: "oauth"` + `configFields` - OAuth against a customer-specific
//     host. The user supplies the endpoint (not a secret) before the
//     OAuth dance; `resolveConfig(values)` validates it and returns
//     `{ issuer, mcpUrl }`, which are stored on the connection record
//     and used in place of static issuer/mcpUrl. Glean is the first.
//
// CORS + OAuth-metadata reachability for each entry was confirmed
// against `Origin: http://localhost:5173` during integration. Servers
// that only expose the legacy split HTTP+SSE transport (Atlassian,
// Intercom, PayPal at `/sse`) need transport changes before they can
// be added. Servers whose MCP endpoint omits CORS headers entirely
// (Slack, Sentry, Stripe) need a backend bridge — they're not
// reachable browser-only.
export const SERVER_REGISTRY = {
  linear: {
    id: "linear",
    name: "Linear",
    auth: "oauth",
    issuer: "https://mcp.linear.app",
    mcpUrl: "https://mcp.linear.app/mcp",
    description: "Issues, projects, comments, and search.",
  },
  notion: {
    id: "notion",
    name: "Notion",
    auth: "oauth",
    issuer: "https://mcp.notion.com",
    mcpUrl: "https://mcp.notion.com/mcp",
    description: "Pages, databases, and search across your Notion workspace.",
  },
  zapier: {
    id: "zapier",
    name: "Zapier",
    auth: "oauth",
    issuer: "https://mcp.zapier.com",
    mcpUrl: "https://mcp.zapier.com/api/mcp/mcp",
    description: "Run any of your Zapier-connected app actions as tools.",
  },
  glean: {
    id: "glean",
    name: "Glean",
    auth: "oauth",
    // Glean hosts one MCP server per customer at
    // https://<instance>-be.glean.com/mcp/<server>; the OAuth
    // authorization server (metadata, /oauth/register, /oauth/authorize,
    // /oauth/token) lives at the same origin. The user copies the URL
    // from Glean's MCP Configurator. Tool calls run as the signed-in
    // Glean user, so Glean's document permissions still apply.
    description: "Enterprise search, chat, and documents from your Glean workspace. Runs with your Glean permissions.",
    configFields: [{
      key: "mcpUrl",
      label: "Glean MCP server URL",
      placeholder: "https://acme-be.glean.com/mcp/default",
      helpUrl: "https://docs.glean.com/user-guide/mcp/end-user-configuration",
      helpLabel: "copy it from Glean's MCP Configurator",
    }],
    resolveConfig: resolveGleanConfig,
  },
  firecrawl: {
    id: "firecrawl",
    name: "Firecrawl",
    auth: "apiKey",
    // API key goes in the URL path. No header auth.
    mcpUrl: "https://mcp.firecrawl.dev/{apiKey}/v2/mcp",
    description: "Scrape, crawl, search, and map the web — clean markdown returned for LLM context.",
    credentialFields: [{
      key: "apiKey",
      label: "API key",
      placeholder: "fc-…",
      helpUrl: "https://www.firecrawl.dev/app/api-keys",
    }],
  },
  tavily: {
    id: "tavily",
    name: "Tavily",
    auth: "apiKey",
    // API key goes in a query parameter on this vendor.
    mcpUrl: "https://mcp.tavily.com/mcp/?tavilyApiKey={apiKey}",
    description: "Search the web with answer-grade snippets purpose-built for LLM agents.",
    credentialFields: [{
      key: "apiKey",
      label: "API key",
      placeholder: "tvly-…",
      helpUrl: "https://app.tavily.com/home",
    }],
  },
  browserbase: {
    id: "browserbase",
    name: "Browserbase",
    auth: "apiKey",
    // Browserbase's hosted MCP needs both an API key and a project id
    // — the key authenticates the account, the project scopes which
    // browser pool the sessions land in. Both go in the URL query.
    mcpUrl: "https://server.smithery.ai/@browserbasehq/mcp-browserbase/mcp?api_key={apiKey}&project_id={projectId}",
    description: "Drive a real headless browser — navigate, click, fill forms, extract content from JS-rendered pages.",
    credentialFields: [
      {
        key: "apiKey",
        label: "API key",
        placeholder: "bb_…",
        helpUrl: "https://www.browserbase.com/settings",
      },
      {
        key: "projectId",
        label: "Project ID",
        placeholder: "uuid",
        helpUrl: "https://www.browserbase.com/settings",
      },
    ],
  },
};

export function listServerDefinitions() {
  return Object.values(SERVER_REGISTRY);
}

export function isApiKeyServer(def) {
  return def?.auth === "apiKey";
}

export function isConfigServer(def) {
  return def?.auth !== "apiKey" && Array.isArray(def?.configFields) && def.configFields.length > 0;
}

const GLEAN_BACKEND_HOST_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?-be\.glean\.com$/;
const GLEAN_MCP_PATH_RE = /^\/mcp\/([A-Za-z0-9_-]+)\/?$/;

// Glean's MCP URL doubles as the OAuth issuer origin. Accept exactly
// the shape Glean's MCP Configurator hands out; anything else is more
// likely a paste mistake than a real endpoint, and a wrong host would
// send the user through a stranger's OAuth flow.
export function resolveGleanConfig(values) {
  const raw = typeof values?.mcpUrl === "string" ? values.mcpUrl.trim() : "";
  if (!raw) throw new Error("Glean MCP server URL is required.");
  let url;
  try {
    url = new URL(raw);
  } catch {
    throw new Error("Enter the full URL, for example https://acme-be.glean.com/mcp/default.");
  }
  if (url.protocol !== "https:") throw new Error("Glean MCP server URL must start with https://.");
  if (!GLEAN_BACKEND_HOST_RE.test(url.hostname)) {
    throw new Error("Host must be your Glean backend, like acme-be.glean.com.");
  }
  if (url.username || url.password || url.port) {
    throw new Error("Glean MCP server URL must not include credentials or a port.");
  }
  const path = GLEAN_MCP_PATH_RE.exec(url.pathname);
  if (!path) throw new Error("Path must be /mcp/<server>, for example /mcp/default.");
  const issuer = `https://${url.hostname}`;
  return {
    config: { mcpUrl: `${issuer}/mcp/${path[1]}` },
    issuer,
    mcpUrl: `${issuer}/mcp/${path[1]}`,
  };
}

// The endpoints a connection talks to. Static for ordinary OAuth
// servers; derived from the saved per-connection config for
// configFields servers (null until the user has saved one); templated
// from stored credentials for apiKey servers (null while any required
// field is empty).
export function resolveEndpoints(def, conn) {
  if (!def) return null;
  if (def.auth === "apiKey") {
    if (!def.mcpUrl) return null;
    const creds = conn?.credentials || {};
    let url = def.mcpUrl;
    for (const field of def.credentialFields || []) {
      const value = creds[field.key];
      if (!value) return null;
      url = url.replaceAll(`{${field.key}}`, encodeURIComponent(value));
    }
    return { issuer: null, mcpUrl: url };
  }
  if (isConfigServer(def)) {
    if (!conn?.config) return null;
    try {
      const resolved = def.resolveConfig(conn.config);
      return { issuer: resolved.issuer, mcpUrl: resolved.mcpUrl };
    } catch {
      return null;
    }
  }
  if (!def.mcpUrl) return null;
  return { issuer: def.issuer, mcpUrl: def.mcpUrl };
}

// Build the MCP URL the transport hits. Returns null when the
// connection is missing or the endpoint cannot be derived yet.
export function resolveMcpUrl(tenantId, serverId) {
  const def = SERVER_REGISTRY[serverId];
  if (!def) return null;
  const needsRecord = def.auth === "apiKey" || isConfigServer(def);
  const conn = needsRecord ? loadConnection(tenantId, serverId) : null;
  return resolveEndpoints(def, conn)?.mcpUrl ?? null;
}

export function getRedirectUri() {
  return `${window.location.origin}/mcp-callback`;
}

function key(tenantId, serverId) {
  const owner = currentBrowserStorageOwner(tenantId);
  return owner == null ? null : `${STORAGE_PREFIX}${owner.key}:${serverId}:conn`;
}

function discardLegacyConnections(tenantId) {
  if (!tenantId || typeof localStorage === "undefined") return;
  const prefix = `${LEGACY_STORAGE_PREFIX}${tenantId}:`;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const candidate = localStorage.key(i);
      if (candidate?.startsWith(prefix) && candidate.endsWith(":conn")) keys.push(candidate);
    }
    for (const candidate of keys) localStorage.removeItem(candidate);
  } catch { /* storage unavailable */ }
}

export function loadConnection(tenantId, serverId) {
  if (!tenantId || !serverId) return null;
  discardLegacyConnections(tenantId);
  try {
    const storageKey = key(tenantId, serverId);
    if (!storageKey) return null;
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveConnection(tenantId, serverId, conn) {
  if (!tenantId || !serverId) return;
  try {
    const storageKey = key(tenantId, serverId);
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify(conn));
    notifyChange();
  } catch {}
}

export function clearConnection(tenantId, serverId) {
  if (!tenantId || !serverId) return;
  try {
    const storageKey = key(tenantId, serverId);
    if (!storageKey) return;
    localStorage.removeItem(storageKey);
    notifyChange();
  } catch {}
}

// Enumerate every connection record for this tenant. Each entry is
// `{ ...serverDef, connection }` so the UI can render it with the
// canonical name + the live token state in one pass.
export function listConnections(tenantId) {
  if (!tenantId) return [];
  const owner = currentBrowserStorageOwner(tenantId);
  if (!owner) return [];
  discardLegacyConnections(tenantId);
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const prefix = `${STORAGE_PREFIX}${owner.key}:`;
      if (!k.startsWith(prefix) || !k.endsWith(":conn")) continue;
      const serverId = k.slice(prefix.length, -":conn".length);
      const def = SERVER_REGISTRY[serverId];
      if (!def) continue;
      const conn = loadConnection(tenantId, serverId);
      if (!conn) continue;
      out.push({ ...def, connection: conn });
    }
  } catch {}
  return out;
}

// Subscribe to add/remove/refresh events. Returns an unsubscribe fn.
export function onConnectionsChanged(cb) {
  const handler = () => cb();
  window.addEventListener(CHANGE_EVENT, handler);
  // localStorage cross-tab signal so other tabs see new connections too.
  // Status-only writes (lastError, refresh outcome) are filtered out so a
  // failing handshake in one tab does not rebuild the chat in every tab.
  const storageHandler = (e) => {
    if (!e.key || !e.key.startsWith(STORAGE_PREFIX)) return;
    if (!membershipChanged(e.oldValue, e.newValue)) return;
    cb();
  };
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener("storage", storageHandler);
  };
}

function notifyChange() {
  try { window.dispatchEvent(new CustomEvent(CHANGE_EVENT)); } catch {}
}

// True unless both values parse and differ only in status bookkeeping.
function membershipChanged(oldRaw, newRaw) {
  if (oldRaw == null || newRaw == null) return true;
  try {
    const strip = (raw) => {
      const conn = JSON.parse(raw);
      const tokens = conn?.tokens ? { ...conn.tokens, refreshFailedAt: undefined } : conn?.tokens;
      return JSON.stringify({ ...conn, lastError: undefined, tokens });
    };
    return strip(oldRaw) !== strip(newRaw);
  } catch {
    return true;
  }
}

// Clamp a server- or network-derived message before it reaches copy the
// dashboard speaks in its own voice: one line, printable, bounded.
export function shortError(err, max = 200) {
  const raw = String(err?.message ?? err ?? "").replace(/[\u0000-\u001f\u007f]+/g, " ").trim();
  return raw.length > max ? `${raw.slice(0, max - 3)}...` : raw;
}

// A user-chosen issuer (config servers) must not be able to point the
// browser, the auth code, or the refresh token anywhere else: every
// endpoint the metadata names has to live on that same https origin.
// Static-issuer servers are left alone; their vendors legitimately host
// the authorize page on another domain.
export function assertTrustedMetadata(def, issuer, metadata) {
  if (!isConfigServer(def)) return;
  const origin = new URL(issuer).origin;
  const check = (label, value) => {
    if (value == null) return;
    let url;
    try { url = new URL(String(value)); } catch { url = null; }
    if (!url || url.protocol !== "https:" || url.origin !== origin) {
      throw new Error(`${def.name}'s sign-in service named an unexpected ${label} (${shortError(value, 80)}). Connection refused.`);
    }
  };
  check("issuer", metadata?.issuer);
  check("authorization endpoint", metadata?.authorization_endpoint);
  check("token endpoint", metadata?.token_endpoint);
  check("registration endpoint", metadata?.registration_endpoint);
}

// Write a record without announcing a membership change. Cross-tab
// listeners still see the storage event; same-tab listeners get
// STATUS_EVENT only.
function saveConnectionQuietly(tenantId, serverId, conn) {
  if (!tenantId || !serverId) return;
  try {
    const storageKey = key(tenantId, serverId);
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify(conn));
    try { window.dispatchEvent(new CustomEvent(STATUS_EVENT)); } catch {}
  } catch {}
}

// Subscribe to status-only writes (see STATUS_EVENT). The picker listens
// to both this and onConnectionsChanged; the chat listens only to the
// latter. Returns an unsubscribe fn.
export function onConnectionStatusChanged(cb) {
  const handler = () => cb();
  window.addEventListener(STATUS_EVENT, handler);
  return () => window.removeEventListener(STATUS_EVENT, handler);
}

// Persist an apiKey-flavour connection. No OAuth dance — the user
// has already pasted the credentials, we just store them and emit
// the change event so the picker + pool refresh. `credentials` is a
// map keyed by the entry's credentialFields[].key values.
export function saveApiKeyConnection(tenantId, serverId, credentials) {
  const def = SERVER_REGISTRY[serverId];
  if (!def) throw new Error(`Unknown MCP server: ${serverId}`);
  if (def.auth !== "apiKey") throw new Error(`${serverId} is not an api-key server`);
  if (!tenantId) throw new Error("No active tenant — cannot connect MCP.");
  const cleaned = {};
  for (const field of def.credentialFields || []) {
    const raw = credentials?.[field.key];
    const trimmed = typeof raw === "string" ? raw.trim() : "";
    if (!trimmed) throw new Error(`${field.label || field.key} is required`);
    cleaned[field.key] = trimmed;
  }
  saveConnection(tenantId, serverId, {
    serverId,
    credentials: cleaned,
    connectedAt: Date.now(),
  });
}

// Persist the endpoint config for a configFields server. Validates
// through the entry's `resolveConfig` (throws a user-facing message on
// a bad value). Tokens, client registration, and OAuth metadata are
// dropped when the endpoint changes: they were minted by the previous
// host and would be replayed against the new one otherwise.
export function saveServerConfig(tenantId, serverId, values) {
  const def = SERVER_REGISTRY[serverId];
  if (!def) throw new Error(`Unknown MCP server: ${serverId}`);
  if (!isConfigServer(def)) throw new Error(`${serverId} does not take endpoint configuration`);
  if (!tenantId) throw new Error("No active tenant. Sign in and try again.");
  const resolved = def.resolveConfig(values);
  const existing = loadConnection(tenantId, serverId);
  const unchanged = existing?.config
    && resolveEndpoints(def, existing)?.mcpUrl === resolved.mcpUrl;
  saveConnection(tenantId, serverId, {
    serverId,
    config: resolved.config,
    clientId: unchanged ? existing.clientId : undefined,
    registration: unchanged ? existing.registration : null,
    metadata: unchanged ? existing.metadata : null,
    tokens: unchanged ? existing.tokens : null,
    lastError: null,
    connectedAt: unchanged ? existing.connectedAt : null,
  });
  return resolved;
}

// Remember why the last handshake failed so the picker can show the
// row as failed with the reason, instead of "connected" with tools
// that never arrive. Cleared by the next successful handshake.
export function recordHandshakeError(tenantId, serverId, message) {
  const conn = loadConnection(tenantId, serverId);
  if (!conn) return;
  const text = String(message || "MCP connection failed").slice(0, 500);
  if (conn.lastError?.message === text) return;
  saveConnectionQuietly(tenantId, serverId, { ...conn, lastError: { message: text, at: Date.now() } });
}

export function clearHandshakeError(tenantId, serverId) {
  const conn = loadConnection(tenantId, serverId);
  if (!conn?.lastError) return;
  saveConnectionQuietly(tenantId, serverId, { ...conn, lastError: null });
}

// One word for the picker. Derived from the stored record only (no
// network): the row's state must be readable before any handshake.
//
//   disconnected  no record, or an OAuth record that never held tokens
//   configured    endpoint saved, never signed in
//   failed        the last handshake or sign-in failed (lastError set)
//   expired       token past expiry (or cleared) and no way to refresh it
//   connected     token valid, or refreshable on the next call
export function connectionStatus(def, conn, now = Date.now()) {
  if (!def || !conn) return "disconnected";
  if (def.auth === "apiKey") return conn.lastError ? "failed" : "connected";
  if (conn.lastError) return "failed";
  const tokens = conn.tokens;
  if (!tokens?.accessToken) {
    if (isConfigServer(def) && conn.config && !conn.connectedAt) return "configured";
    // Tokens were cleared after a failed refresh on a server that had
    // been connected: the sign-in is gone, not the connection record.
    return conn.connectedAt ? "expired" : "disconnected";
  }
  const { expiresAt, refreshToken, refreshFailedAt } = tokens;
  if (expiresAt && expiresAt <= now) {
    if (!refreshToken || refreshFailedAt) return "expired";
  }
  return "connected";
}

// Top-level: kick off an OAuth dance for a given (tenant, server).
// Persists the freshly-registered client_id + auth metadata before
// redirecting so the callback page has everything it needs without
// hitting the network again.
export async function startConnect(tenantId, serverId, { signal } = {}) {
  const def = SERVER_REGISTRY[serverId];
  if (!def) throw new Error(`Unknown MCP server: ${serverId}`);
  if (!tenantId) throw new Error("No active tenant — cannot connect MCP.");
  const owner = currentBrowserStorageOwner(tenantId);
  if (!owner) throw new Error("Verified tenant and user identity are required to connect MCP.");
  const existing = loadConnection(tenantId, serverId);
  const endpoints = resolveEndpoints(def, existing);
  if (!endpoints?.issuer) {
    throw new Error(`${def.name} needs its server URL before it can connect.`);
  }
  let metadata;
  try {
    metadata = await fetchAuthMetadata(endpoints.issuer, { signal });
    assertTrustedMetadata(def, endpoints.issuer, metadata);
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    const message = /Connection refused\.$/.test(err?.message || "")
      ? err.message
      : isConfigServer(def)
        ? `Could not reach ${def.name}'s sign-in service at ${endpoints.issuer} (${shortError(err)}). Check the server URL.`
        : `Could not reach ${def.name}'s sign-in service (${shortError(err)}).`;
    recordHandshakeError(tenantId, serverId, message);
    throw new Error(message);
  }
  if (!browserStorageOwnerIsCurrent(owner)) throw new Error("Origin identity changed while connecting MCP.");
  const redirectUri = getRedirectUri();

  // Re-use the registration when present so we don't accumulate a
  // new client_id on every connect. DCR isn't expensive but it's
  // also not free, and most servers cap how many clients you can
  // register before rate-limiting.
  let clientId = existing?.clientId;
  let registration = existing?.registration ?? null;
  if (!clientId || !registration) {
    try {
      registration = await registerClient(metadata, {
        redirectUri,
        clientName: `origin (${tenantId})`,
        signal,
      });
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      // Glean and similar servers let admins restrict which redirect
      // URIs may self-register; that comes back as a 4xx here.
      const message = `${def.name} refused to register this app (${shortError(err)}). `
        + `An administrator may need to allow the redirect URI ${redirectUri} or register a static client.`;
      recordHandshakeError(tenantId, serverId, message);
      throw new Error(message);
    }
    clientId = registration.client_id;
    if (!browserStorageOwnerIsCurrent(owner)) throw new Error("Origin identity changed while connecting MCP.");
  }
  saveConnection(tenantId, serverId, {
    serverId,
    config: existing?.config ?? undefined,
    clientId,
    registration,
    metadata,
    // tokens left as-is on re-connect; the callback page replaces them.
    tokens: existing?.tokens ?? null,
    lastError: null,
    connectedAt: existing?.connectedAt ?? null,
  });
  await startAuthorize({
    metadata,
    clientId,
    redirectUri,
    scope: undefined,
    ctx: { tenantId, serverId, issuer: endpoints.issuer },
  });
}

// Called from the callback page after `exchangeCode` resolves. Merges
// the new tokens into the existing record and notifies subscribers.
export function recordTokens(tenantId, serverId, tokens, { issuer } = {}) {
  const owner = currentBrowserStorageOwner(tenantId);
  if (!owner) {
    throw new Error(
      "MCP callback cannot save credentials until the initiating dashboard identity is verified. Return to the dashboard and reconnect.",
    );
  }
  const existing = loadConnection(tenantId, serverId);
  if (!existing) throw new Error(`No pending connection record for ${serverId}`);
  // The sign-in started against one host; if the saved URL moved to
  // another host meanwhile (another tab's Edit URL), these tokens belong
  // to the old host and must not be sent to the new one.
  const def = SERVER_REGISTRY[serverId];
  if (issuer && def && isConfigServer(def)) {
    const current = resolveEndpoints(def, existing)?.issuer;
    if (current !== issuer) {
      throw new Error(`${def.name}'s server URL changed during sign-in. Open the MCP tab and connect again.`);
    }
  }
  const merged = {
    ...existing,
    tokens: {
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token || existing.tokens?.refreshToken || null,
      expiresAt: tokens.expires_in ? Date.now() + Number(tokens.expires_in) * 1000 : null,
      tokenType: tokens.token_type || "Bearer",
    },
    lastError: null,
    connectedAt: Date.now(),
  };
  if (!browserStorageOwnerIsCurrent(owner)) {
    throw new Error("Origin identity changed before MCP credentials could be saved.");
  }
  saveConnection(tenantId, serverId, merged);
  return merged;
}

const REFRESH_SAFETY_MS = 30_000;

// Returns a usable bearer token for the transport, refreshing OAuth
// tokens if needed. For apiKey servers, returns the stored credential
// referenced by `bearerField` (or null when the URL template already
// carries the credential — vendors like Firecrawl that bake the key
// into the path don't need an auth header). Returns null when there's
// no path forward so callers can surface a "reconnect" prompt instead
// of looping on 401s.
export async function getValidAccessToken(tenantId, serverId) {
  const def = SERVER_REGISTRY[serverId];
  if (!def) return null;
  if (def.auth === "apiKey") {
    if (!def.bearerField) return null; // URL templating handles auth.
    const conn = loadConnection(tenantId, serverId);
    const cred = conn?.credentials?.[def.bearerField];
    return cred || null;
  }
  const conn = loadConnection(tenantId, serverId);
  if (!conn?.tokens?.accessToken) return null;
  const { expiresAt, refreshToken } = conn.tokens;
  if (!expiresAt || expiresAt - REFRESH_SAFETY_MS > Date.now()) {
    return conn.tokens.accessToken;
  }
  if (!refreshToken) {
    // Token expired and we can't refresh — clear it so the next
    // call surfaces a reconnect prompt rather than a stale 401.
    saveConnectionQuietly(tenantId, serverId, { ...conn, tokens: null });
    return null;
  }
  const owner = currentBrowserStorageOwner(tenantId);
  if (!owner) return null;
  try {
    const issuer = resolveEndpoints(def, conn)?.issuer;
    if (issuer) assertTrustedMetadata(def, issuer, conn.metadata);
    const refreshed = await refreshTokens({
      refreshToken,
      metadata: conn.metadata,
      clientId: conn.clientId,
    });
    if (!browserStorageOwnerIsCurrent(owner)) return null;
    const updated = {
      ...conn,
      tokens: {
        accessToken: refreshed.access_token,
        refreshToken: refreshed.refresh_token || refreshToken,
        expiresAt: refreshed.expires_in ? Date.now() + Number(refreshed.expires_in) * 1000 : null,
        tokenType: refreshed.token_type || "Bearer",
        refreshFailedAt: null,
      },
    };
    saveConnection(tenantId, serverId, updated);
    return updated.tokens.accessToken;
  } catch (err) {
    console.warn(`[mcp] ${serverId} refresh failed:`, err);
    // Leave the tokens in place (a transient failure may clear up) but
    // mark the refresh as rejected so the picker reports "expired" and
    // offers Reconnect rather than showing a dead connection as live.
    if (!browserStorageOwnerIsCurrent(owner)) return null;
    const latest = loadConnection(tenantId, serverId);
    if (latest?.tokens && !latest.tokens.refreshFailedAt) {
      saveConnectionQuietly(tenantId, serverId, {
        ...latest,
        tokens: { ...latest.tokens, refreshFailedAt: Date.now() },
      });
    }
    return null;
  }
}
